import React, { useEffect, useState } from "react";
import GoBack from "components/atoms/GoBack";
import Container from "@mui/material/Container";
import Banner from "components/templates/Banner";
import { useNavigate, useParams } from "react-router-dom";
import CircleLoader from "components/atoms/CircleLoader";
import AddRolesPermissionsForm from "./AddRolesPermissionsForm/AddRolesPermissionsForm";
import { Permissions, permissionCheckList } from "./AddRolesPermissionsForm";
import ToasterService from "utils/toaster.util";
import RoleService from "services/role.service";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { change } from "redux-form";

const REMOVED_PRIVILEGE_NAMES = new Set(["rental_comparison"]);

function withoutRemovedPrivileges(list: Permissions[]): Permissions[] {
  return list.filter(
    (entry) => !REMOVED_PRIVILEGE_NAMES.has(String(entry.name))
  );
}

function mergePermissionLists(
  existing: Permissions[],
  master: Permissions[]
): Permissions[] {
  const existingByName = new Map(
    withoutRemovedPrivileges(existing).map((entry) => [String(entry.name), entry])
  );

  // Overlay the saved flags onto the master catalog entry. The master always
  // supplies the route lists (viewRoutes/addRoutes/editRoutes/deleteRoutes) and
  // which columns apply (null = N/A), so the editor stays correct even as the
  // catalog grows. Legacy-shaped saved entries (read/write only) are tolerated:
  // `??` falls back to deriving view/read from `read` and add/edit from `write`.
  return withoutRemovedPrivileges(master).map((masterEntry: any) => {
    const saved: any = existingByName.get(String(masterEntry.name));
    if (!saved) return { ...masterEntry };
    return {
      ...masterEntry,
      view:
        masterEntry.view === null
          ? null
          : saved.view ?? !!saved.read,
      read: masterEntry.read === null ? null : saved.read ?? false,
      add:
        masterEntry.add === null
          ? null
          : saved.add ?? !!saved.write,
      edit:
        masterEntry.edit === null
          ? null
          : saved.edit ?? !!saved.write,
      delete:
        masterEntry.delete === null ? null : saved.delete ?? false,
    };
  });
}

const AddRolesPermissions = () => {
  const form = "AddRolesPermissionsForm";
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.role.loading);
  const privilege = useAppSelector((state) => state.role.privilege);

  const [permissionsList, setPermissionList] = useState<Permissions[]>([]);

  useEffect(() => {
    if (params?.id) {
      RoleService.getNewRole(params?.id);
    }
  }, [params]);
  useEffect(() => {
    if (params?.id) {
      if (privilege?.privilegies) {
        dispatch(change(form, "role_name", privilege.title));
        let array: any =
          privilege?.privilegies.length === 0
            ? withoutRemovedPrivileges(permissionCheckList)
            : mergePermissionLists(privilege.privilegies, permissionCheckList);
        let persmissions: any = JSON.stringify(array);
        setPermissionList(JSON.parse(persmissions));
      }
      return;
    }
    setPermissionList(withoutRemovedPrivileges(permissionCheckList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privilege]);
  const handleSubmit = (values: any) => {
    // Derive the flat `permissions` strings and `routes` straight from the
    // privilege objects (permissionsList) — the single source of truth for the
    // checkboxes. Emits one string per enabled flag (view/read/add/edit/delete)
    // and unions the matching route lists. Mirrors the backend derivation in
    // utils/derive_role_access.utils.js so the two can never drift.
    const permisions: string[] = [];
    const routesSet = new Set<string>();
    permissionsList.forEach((item: any) => {
      if (!item?.name) return;
      if (item.view) {
        permisions.push(`${item.name}:view`);
        (item.viewRoutes || []).forEach((r: string) => routesSet.add(r));
      }
      if (item.read) {
        permisions.push(`${item.name}:read`);
      }
      if (item.add) {
        permisions.push(`${item.name}:add`);
        (item.addRoutes || []).forEach((r: string) => routesSet.add(r));
      }
      if (item.edit) {
        permisions.push(`${item.name}:edit`);
        (item.editRoutes || []).forEach((r: string) => routesSet.add(r));
      }
      if (item.delete) {
        permisions.push(`${item.name}:delete`);
        (item.deleteRoutes || []).forEach((r: string) => routesSet.add(r));
      }
    });
    const permisionsUnique = Array.from(new Set(permisions));
    const routes = Array.from(routesSet);

    if (permisionsUnique.length === 0) {
      ToasterService.showError("Please Select atleast one privilege");
      return;
    }

    const payload = {
      permissions: permisionsUnique,
      privilegies: permissionsList,
      title: values.role_name,
      routes: routes,
    };
    if (params?.id) RoleService.updateNewRole(payload, params?.id, navigate);
    else RoleService.addNewRole(payload, navigate);
  };

  const handleChange = (e: any, permission: String) => {
    if (!e || Object.keys(e).length === 0) return;
    setPermissionList((prev) =>
      prev.map((item: any) => {
        if (item.name !== e.name) return item;
        const updated = { ...item, [permission as string]: !item[permission as string] };
        // View auto-checks Read: seeing the page implies loading its data, so
        // turning View on also turns Read on. Turning View off leaves Read as-is,
        // and checking Read never auto-checks View. Add/Edit are independent.
        if (permission === "view" && updated.view) updated.read = true;
        return updated;
      })
    );
  };

  return (
    <Container>
      <GoBack path="/roles" title="Back to Roles" />
      <Banner heading="Add Role Permissions" />

      {loading && <CircleLoader />}
      {/* @ts-ignore */}
      <AddRolesPermissionsForm
        onSubmit={handleSubmit}
        //@ts-ignore
        permissionsList={permissionsList}
        handleChange={handleChange}
      />
    </Container>
  );
};

export default AddRolesPermissions;
